import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { InputerrorsComponent } from '../../inputerrors.component';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-auth-register',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, InputerrorsComponent],
  template: `
    <div class="min-h-screen flex flex-col items-center justify-center bg-benzGrey-50 px-4 py-8">
      <div class="w-full max-w-md">
        
        <!-- Back Button -->
        <button
          type="button"
          (click)="goBack()"
          class="mb-6 flex items-center gap-2 text-benzGrey-600 hover:text-benzGrey-900 transition-colors focus:outline-none focus:ring-2 focus:ring-benzRed-700 rounded-lg p-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
          </svg>
          <span>Zurück</span>
        </button>

        @if (isCheckingAuth) {
          <!-- Loading State -->
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <div class="flex flex-col items-center justify-center py-12">
              <svg class="animate-spin h-12 w-12 text-benzRed-700 mb-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-benzGrey-600">Anmeldestatus wird überprüft...</p>
            </div>
          </div>
        } @else if (authService.isAuthenticated()) {
          <!-- Already Logged In Card -->
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <div class="text-center mb-8">
              <div class="w-16 h-16 mx-auto mb-4 bg-benzRed-100 rounded-full flex items-center justify-center">
                <svg class="w-8 h-8 text-benzRed-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h1 class="text-3xl font-bold text-benzGrey-900 mb-2">Bereits angemeldet</h1>
              <p class="text-benzGrey-600 mb-1">Sie sind bereits eingeloggt als</p>
              <p class="text-lg font-semibold text-benzRed-700">{{ authService.currentUser()?.username || 'Benutzer' }}</p>
            </div>

            <div class="flex flex-col gap-3">
              <button
                type="button"
                (click)="continueWithCurrentAccount()"
                class="w-full px-6 py-4 bg-benzRed-700 text-white text-lg font-semibold rounded-lg hover:bg-benzRed-800 transition-colors focus:outline-none focus:ring-4 focus:ring-benzRed-300">
                Mit diesem Konto fortfahren
              </button>
              
              <button
                type="button"
                (click)="logoutAndShowRegister()"
                class="w-full px-6 py-4 bg-white text-benzGrey-900 text-lg font-semibold border-2 border-benzGrey-900 rounded-lg hover:bg-benzGrey-50 transition-colors focus:outline-none focus:ring-4 focus:ring-benzGrey-300">
                Abmelden und neu registrieren
              </button>
            </div>

            <div class="mt-6 p-4 bg-benzGrey-50 rounded-lg">
              <p class="text-sm text-benzGrey-600 text-center">
                Sie können nicht gleichzeitig zwei Konten haben. Bitte melden Sie sich ab, wenn Sie ein neues Konto erstellen möchten.
              </p>
            </div>
          </div>
        } @else {
          <!-- Normal Registration Card -->
          <div class="bg-white rounded-2xl shadow-lg p-8">
          
          <!-- Header -->
          <div class="text-center mb-8">
            <h1 class="text-3xl font-bold text-benzGrey-900 mb-2">Registrieren</h1>
            <p class="text-benzGrey-600">Erstellen Sie Ihr kostenloses Konto</p>
          </div>

          <!-- Benefits -->
          <div class="mb-6 p-4 bg-benzRed-50 rounded-lg">
            <p class="text-sm font-semibold text-benzRed-900 mb-2">Ihre Vorteile:</p>
            <ul class="text-sm text-benzRed-800 space-y-1">
              <li class="flex items-center gap-2">
                <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span>Schnellerer Checkout-Prozess</span>
              </li>
              <li class="flex items-center gap-2">
                <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span>Überblick über alle Bestellungen</span>
              </li>
              <li class="flex items-center gap-2">
                <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span>Bequeme Verwaltung Ihrer Daten</span>
              </li>
            </ul>
          </div>

          <!-- Error Messages -->
          @if (errorMessage()) {
            <div class="mb-6 p-4 bg-benzRed-50 border border-benzRed-200 rounded-lg" role="alert">
              <div class="flex items-start gap-3">
                <svg class="w-5 h-5 text-benzRed-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
                </svg>
                <p class="text-sm text-benzRed-700">{{ errorMessage() }}</p>
              </div>
            </div>
          }

          <!-- Register Form -->
          <form [formGroup]="registerForm" (ngSubmit)="processRegister()" class="flex flex-col gap-4">
            
            <!-- Username -->
            <app-inputerrors [control]="registerForm.controls.username">
              <label class="flex flex-col gap-2">
                <span class="text-sm font-medium text-benzGrey-900">
                  Benutzername <span class="text-benzRed-700">*</span>
                </span>
                <input
                  type="text"
                  formControlName="username"
                  placeholder="Mindestens 8 Zeichen"
                  [class.border-benzRed-700]="registerForm.controls.username.touched && !registerForm.controls.username.valid"
                  class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700 transition-all"
                  required>
                @if (registerForm.controls.username.touched && registerForm.controls.username.value) {
                  <p class="text-xs text-benzGrey-600">
                    Mindestens 8 Zeichen
                  </p>
                }
              </label>
            </app-inputerrors>

            <!-- Email -->
            <app-inputerrors [control]="registerForm.controls.email">
              <label class="flex flex-col gap-2">
                <span class="text-sm font-medium text-benzGrey-900">
                  E-Mail-Adresse <span class="text-benzRed-700">*</span>
                </span>
                <input
                  type="email"
                  formControlName="email"
                  placeholder="max@example.com"
                  [class.border-benzRed-700]="registerForm.controls.email.touched && !registerForm.controls.email.valid"
                  class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700 transition-all"
                  required>
              </label>
            </app-inputerrors>

            <!-- Password -->
            <app-inputerrors [control]="registerForm.controls.password">
              <label class="flex flex-col gap-2">
                <span class="text-sm font-medium text-benzGrey-900">
                  Passwort <span class="text-benzRed-700">*</span>
                </span>
                <div class="relative">
                  <input
                    [type]="showPassword ? 'text' : 'password'"
                    formControlName="password"
                    placeholder="Mindestens 10 Zeichen"
                    [class.border-benzRed-700]="registerForm.controls.password.touched && !registerForm.controls.password.valid"
                    class="w-full border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-benzRed-700 transition-all"
                    required>
                  <button
                    type="button"
                    (click)="togglePasswordVisibility()"
                    class="absolute right-3 top-1/2 -translate-y-1/2 text-benzGrey-600 hover:text-benzGrey-900 focus:outline-none focus:ring-2 focus:ring-benzRed-700 rounded p-1">
                    @if (showPassword) {
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"></path>
                      </svg>
                    } @else {
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                    }
                  </button>
                </div>
                @if (registerForm.controls.password.touched && registerForm.controls.password.value) {
                  <p class="text-xs text-benzGrey-600">
                    Mindestens 10 Zeichen
                  </p>
                }
              </label>
            </app-inputerrors>

            <!-- Password Confirmation -->
            <app-inputerrors [control]="registerForm.controls.password_confirmation">
              <label class="flex flex-col gap-2">
                <span class="text-sm font-medium text-benzGrey-900">
                  Passwort bestätigen <span class="text-benzRed-700">*</span>
                </span>
                <input
                  [type]="showPassword ? 'text' : 'password'"
                  formControlName="password_confirmation"
                  placeholder="Passwort wiederholen"
                  [class.border-benzRed-700]="registerForm.controls.password_confirmation.touched && !registerForm.controls.password_confirmation.valid"
                  class="border-2 border-benzGrey-900 placeholder:font-light placeholder:text-benzGrey-600 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-benzRed-700 transition-all"
                  required>
              </label>
            </app-inputerrors>

            <!-- Terms and Conditions -->
            <label class="flex gap-3 items-start cursor-pointer p-3 rounded-lg hover:bg-benzGrey-50 transition-colors">
              <input
                type="checkbox"
                formControlName="acceptTerms"
                class="mt-1 border-2 border-benzGrey-900 scale-125 accent-benzRed-700 focus:outline-none focus:ring-2 focus:ring-benzRed-700 cursor-pointer">
              <span class="text-sm text-benzGrey-700">
                Ich akzeptiere die <a href="https://www.beimbenz.de/agb" target="_blank" class="text-benzRed-700 hover:underline">AGB</a> und <a href="https://www.beimbenz.de/datenschutz" target="_blank" class="text-benzRed-700 hover:underline">Datenschutzerklärung</a> <span class="text-benzRed-700">*</span>
              </span>
            </label>

            <!-- Submit Button -->
            <button
              type="submit"
              [disabled]="isLoading() || !registerForm.valid"
              class="w-full mt-2 px-6 py-4 bg-benzRed-700 text-white text-lg font-semibold rounded-lg hover:bg-benzRed-800 disabled:bg-benzGrey-300 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-4 focus:ring-benzRed-300">
              @if (isLoading()) {
                <span class="flex items-center justify-center gap-2">
                  <svg class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Registrieren...</span>
                </span>
              } @else {
                <span>Jetzt registrieren</span>
              }
            </button>
          </form>

          <!-- Divider -->
          <div class="relative my-8">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-benzGrey-200"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-4 bg-white text-benzGrey-600">oder</span>
            </div>
          </div>

          <!-- Login Link -->
          <div class="text-center">
            <p class="text-sm text-benzGrey-600 mb-2">
              Sie haben bereits ein Konto?
            </p>
            <a
              [routerLink]="['/auth/login']"
              class="text-benzRed-700 font-semibold hover:text-benzRed-800 hover:underline focus:outline-none focus:ring-2 focus:ring-benzRed-700 rounded">
              Jetzt anmelden
            </a>
          </div>
        </div>

        <!-- Guest Option -->
        <div class="mt-6 text-center">
          <button
            type="button"
            (click)="continueAsGuest()"
            class="text-sm text-benzGrey-600 hover:text-benzGrey-900 hover:underline focus:outline-none focus:ring-2 focus:ring-benzGrey-900 rounded px-2 py-1">
            Als Gast fortfahren
          </button>
        </div>
        }
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class AuthRegisterComponent implements OnInit {
  private router = inject(Router);
  public authService = inject(AuthService);

  public registerForm = new FormGroup({
    username: new FormControl('', [Validators.required, Validators.minLength(8)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required, Validators.minLength(10)]),
    password_confirmation: new FormControl('', [Validators.required, Validators.minLength(10)]),
    customer_type: new FormControl('person', [Validators.required]),
    acceptTerms: new FormControl(false, [Validators.requiredTrue]),
  });

  public isLoading = signal(false);
  public errorMessage = signal<string | null>(null);
  public showPassword = false;
  public isCheckingAuth = true;

  ngOnInit(): void {
    // Always check auth status when this component loads
    const bookingMode = sessionStorage.getItem('bookingMode');
    
    if (bookingMode === 'authenticated') {
      // User claims to be authenticated → verify with backend
      this.authService.checkUserLoggedIn().subscribe({
        next: (response) => {
          if (response.logged_in && response.user) {
            // Update auth service state
            this.authService.isAuthenticated.set(true);
            this.authService.currentUser.set(response.user);
            
            // Store/update user info
            sessionStorage.setItem('userId', response.user.id.toString());
            sessionStorage.setItem('userName', response.user.username);
            if (response.user.email) {
              sessionStorage.setItem('userEmail', response.user.email);
            }
          } else {
            // Session expired → logout
            this.authService.logout();
          }
          this.isCheckingAuth = false;
        },
        error: () => {
          // Backend check failed → logout
          this.authService.logout();
          this.isCheckingAuth = false;
        }
      });
    } else {
      // No auth claim → show normal register form
      this.isCheckingAuth = false;
    }
  }

  /**
   * Continue booking with current account
   */
  continueWithCurrentAccount(): void {
    const eventSlug = sessionStorage.getItem('selectedEventSlug');
    if (eventSlug) {
      this.router.navigate(['/booking', eventSlug]);
    } else {
      this.router.navigate(['/']);
    }
  }

  /**
   * Logout and show registration form
   */
  logoutAndShowRegister(): void {
    this.authService.logout();
    // The template will automatically show the registration form after logout
    // because authService.isAuthenticated() will return false
  }

  processRegister(): void {
    if (!this.registerForm.valid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const username = this.registerForm.controls.username.value!;
    const email = this.registerForm.controls.email.value!;
    const password = this.registerForm.controls.password.value!;
    const password_confirmation = this.registerForm.controls.password_confirmation.value!;
    const customer_type = this.registerForm.controls.customer_type.value!;

    this.authService.register({ username, email, password, password_confirmation, customer_type }).subscribe({
      next: (response) => {
        // Navigate to the selected event
        const eventSlug = sessionStorage.getItem('selectedEventSlug');
        
        if (eventSlug) {
          this.router.navigate(['/booking', eventSlug]);
        } else {
          this.router.navigate(['/']);
        }
      },
      error: (error) => {
        this.errorMessage.set(error.message || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
        this.isLoading.set(false);
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  continueAsGuest(): void {
    sessionStorage.setItem('bookingMode', 'guest');
    const eventSlug = sessionStorage.getItem('selectedEventSlug');
    if (eventSlug) {
      this.router.navigate(['/booking', eventSlug]);
    } else {
      this.router.navigate(['/']);
    }
  }

  goBack(): void {
    this.router.navigate(['/auth']);
  }
}

